import { getShareIdForEvent } from './favorites';
import { getEventSearchTitle } from './eventSearchThemes';

const SITE_ORIGIN = 'https://www.myeventsbridge.com';
const SCRIPT_ID = 'event-list-json-ld';

function setMeta(selector, attribute, value, content) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

export function buildDiscoverySeo({ danceType = 'All', location = '' } = {}) {
  const hasType = danceType && danceType !== 'All';
  const typeLabel = hasType ? danceType : 'Dance';

  if (location && hasType) {
    return {
      heading: `${typeLabel} events in ${location}`,
      title: `${typeLabel} Events in ${location} | My Events Bridge`,
      description: `Find upcoming ${typeLabel.toLowerCase()} performances and dance events in ${location}, California. Compare dates, venues, and official ticket links on My Events Bridge.`,
    };
  }
  if (location) {
    return {
      heading: `Dance events in ${location}`,
      title: `Dance Events in ${location} | My Events Bridge`,
      description: `Discover upcoming dance events and performances in ${location}, California, with dates, venues, event details, and official ticket links.`,
    };
  }
  if (hasType) {
    return {
      heading: `Upcoming ${typeLabel} in the Bay Area`,
      title: `${typeLabel} Events in the San Francisco Bay Area | My Events Bridge`,
      description: `Discover upcoming ${typeLabel.toLowerCase()} events across San Francisco, Walnut Creek, Mountain View, and the Bay Area, with official ticket links.`,
    };
  }
  return {
    heading: 'Find Your Rhythm',
    title: 'Find Dance Events in the San Francisco Bay Area | My Events Bridge',
    description: 'Discover upcoming dance events across the San Francisco Bay Area, including contemporary ballet, tango, salsa, fusion, and contact improvisation.',
  };
}

export function applyEventListSeo(events, options = {}) {
  const { danceType = 'All', location = '', indexable = true } = options;
  const seo = buildDiscoverySeo({ danceType, location });
  const canonicalPath = window.location.pathname.endsWith('/')
    ? window.location.pathname
    : `${window.location.pathname}/`;
  const canonicalUrl = `${SITE_ORIGIN}${canonicalPath}`;

  document.title = seo.title;
  setMeta('meta[name="description"]', 'name', 'description', seo.description);
  setMeta('meta[property="og:title"]', 'property', 'og:title', seo.title);
  setMeta('meta[property="og:description"]', 'property', 'og:description', seo.description);
  setMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
  setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', seo.title);
  setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', seo.description);
  setMeta('meta[name="robots"]', 'name', 'robots', indexable ? 'index,follow' : 'noindex,follow');

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', canonicalUrl);

  let script = document.getElementById(SCRIPT_ID);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = SCRIPT_ID;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: seo.heading,
    description: seo.description,
    url: canonicalUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: 'My Events Bridge',
      url: SITE_ORIGIN,
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: events.length,
      itemListElement: events.slice(0, 50).map((event, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: getEventSearchTitle(event),
        url: `${SITE_ORIGIN}/app/event/${getShareIdForEvent(event)}/`,
      })),
    },
  });

  return seo;
}
